import './SearchPage.css';
import { IoSearchOutline } from "react-icons/io5";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef, useCallback } from "react";
import { API_BASE_URL } from '../../utils';
import defaultImage from "../../Assets/partyimage1.jpg";
import { IoClose } from "react-icons/io5";
import { use } from 'react';
import { set } from 'date-fns';
import NoResultsFound from './NoResultsFound';
import SerachCategories from './SerachCategories';
import { useGeoLocation } from '../../context/LocationContext';
import { useQuery } from "@tanstack/react-query";


const url = `${API_BASE_URL}/master/assets/uploads/`;


export default function SearchPage() {

  const { latitude, longitude } = useGeoLocation();

  const [searchInput, setSearchInput] = useState("");
  const [selectedOption, setSelectedOption] = useState(0);
  const [isNoResults, setIsNoResults] = useState(false);

  const [fetchedData, setFetchedData] = useState({
    clubs: [],
    packages: [],
    events: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);
  const cancelTokenRef = useRef(null);

  const searchParams = new URLSearchParams(location.search);
  const decodedString = searchParams.get('q') || '';

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const query = decodeURIComponent(searchParams.get('q') || '');

    console.log("Decoded Search Query:", query);
    setSearchInput(query);
  }, []);



  const fetchData = useCallback(async (lat, long, searchInput, cancelToken) => {
    if (!searchInput || !lat || !long) {
      return;
    }
    setLoading(true);
    setShowSuggestions(false);

    try {
      const formdata = new FormData();
      formdata.append('keyword', searchInput);
      formdata.append('latitude', lat);
      formdata.append('longitude', long);
      const res = await axios.post(`${API_BASE_URL}/master/APIs/Web/globalSearch`, formdata, {
        cancelToken: cancelToken.token
      });

      const { status, data } = res.data;
      if (status === true) {

        const trimmedKeyword = searchInput.trim();
        if (trimmedKeyword) {
          const existing = JSON.parse(localStorage.getItem('recentKeywords')) || [];
          const updated = [trimmedKeyword, ...existing.filter(k => k !== trimmedKeyword)].slice(0, 5);
          localStorage.setItem('recentKeywords', JSON.stringify(updated));
        }

        const clubs = data[0]?.clubs || [];
        const packages = data[1]?.packages || [];
        const events = data[2]?.events || [];
        setFetchedData({ clubs, packages, events });
        setIsNoResults(clubs.length === 0 && packages.length === 0 && events.length === 0);
        setError(null);
      } else {
        setError('Failed to load data');
        console.error('Error:', res.data);
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Request canceled:', error.message);
      } else {
        console.log('Fetch Error:', error);
        setError('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  }, []);



  useEffect(() => {
    const handler = setTimeout(() => {
      if (latitude && longitude && searchInput) {
        if (cancelTokenRef.current) {
          cancelTokenRef.current.cancel('Cancelled due to new request');
        }
        cancelTokenRef.current = axios.CancelToken.source();

        navigate(`/result?q=${searchInput}`);
        fetchData(latitude, longitude, searchInput, cancelTokenRef.current);
      }
    }, 500);

    return () => {
      clearTimeout(handler);
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel();
      }
    };
  }, [searchInput, latitude, longitude, fetchData]);


  useEffect(() => {
    setSearchInput(decodedString);
  }, [decodedString]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  const handleInputChange = (e) => {
    setSearchInput(e.target.value);
    console.log("Search Input:", e.target.value);
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      const removeSpace = searchInput.replace(/\s/g, '');
      if (removeSpace === "") {
        return alert(`It looks like you didn't search.`);
      }
      setShowSuggestions(false);
      navigate(`/result?q=${searchInput}`);
    }
  };

  const [recentKeywords, setRecentKeywords] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const handleSearchFocus = () => {
    const stored = JSON.parse(localStorage.getItem('recentKeywords')) || [];
    setRecentKeywords(stored);
    setShowSuggestions(true);
  };

  const handleDeleteKeyword = (keyword) => {
    const updated = recentKeywords.filter((k) => k !== keyword);
    setRecentKeywords(updated);
    localStorage.setItem('recentKeywords', JSON.stringify(updated));
  };


  function generateClubUrl(clubName, cityName, areaName) {
    const format = (str) => (str ? str.trim().replace(/\s+/g, "-").toLowerCase() : "");
    return `/${format(clubName)}-${format(cityName)}-${format(areaName)}`;
  }

  function generatePackageUrl(clubName, cityName, areaName, title) {
    const format = (str) => (str ? str.trim().replace(/\s+/g, "-").toLowerCase() : "");

    return `/party-package/${format(clubName)}-${format(cityName)}-${format(areaName)}/${format(title)}`;
  }

  function generateEventUrl(clubName, cityName, areaName, title) {
    const format = (str) => (str ? str.trim().replace(/\s+/g, "-").toLowerCase() : "");

    return `/event/${format(clubName)}-${format(cityName)}-${format(areaName)}/${format(title)}`;
  }

  // recommended club
  // ✅ Fetch Clubs
  const fetchRecommendedClubs = async () => {
    const formData = new FormData();
    formData.append("latitude", latitude);
    formData.append("longitude", longitude);

    const { data } = await axios.post(
      `${API_BASE_URL}/master/APIs/Common/recommendedClub`,
      formData
    );
    return data.status ? data.data : [];
  };

  // ✅ Fetch Events
  const fetchUpcomingEvents = async () => {
    const formData = new FormData();
    formData.append("latitude", latitude);
    formData.append("longitude", longitude);

    const { data } = await axios.post(
      `${API_BASE_URL}/master/APIs/ClubEvent/allEvents`,
      formData
    );
    return data.status ? data.data : [];
  };

  // ✅ Fetch Packages
  const fetchRecommendedPackages = async () => {
    const { data } = await axios.get(
      `${API_BASE_URL}/master/APIs/Common/getAllPackageTitle`
    );
    return data.status ? data.data : [];
  };

  // 🏠 Recommended Clubs
  const {
    data: reccommendedclub = [],
    isLoading: isLoadingClubs,
    error: errorClubs,
  } = useQuery({
    queryKey: ["recommendedClubs", latitude, longitude],
    queryFn: fetchRecommendedClubs,
    enabled: !!latitude && !!longitude,
    keepPreviousData: true,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  // 🎉 Upcoming Events
  const {
    data: recommendedEvents = [],
    isLoading: isLoadingEvents,
    error: errorEvents,
  } = useQuery({
    queryKey: ["upcomingEvents", latitude, longitude],
    queryFn: fetchUpcomingEvents,
    enabled: !!latitude && !!longitude,
    keepPreviousData: true,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  // 🎁 Recommended Packages
  const {
    data: recommendedPackages = [],
    isLoading: isLoadingPackages,
    error: errorPackages,
  } = useQuery({
    queryKey: ["recommendedPackages"],
    queryFn: fetchRecommendedPackages,
    keepPreviousData: true,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });




  console.log("recommendedPackages", recommendedPackages);
  console.log("recommendedEvents ", recommendedEvents);
  console.log("reccommendedclub ", reccommendedclub);



  return (
    <div className='search-page-section '>
      <div className="search-results-container">
        <div className='search-results-input-wrapper'>
          <div className="search-bar">
            <input type="search"
              value={searchInput}
              onChange={handleInputChange}
              onFocus={handleSearchFocus}
              onClick={handleSearchFocus}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 500)}
              placeholder="Search clubs, packages..."
              ref={inputRef}
              onKeyDown={handleSearch}
            />
            <span onClick={handleSearch}><IoSearchOutline /></span>


          </div>

          {showSuggestions && recentKeywords.length > 0 && (
            <ul className="search-results-search-suggestions">
              {recentKeywords.map((item, index) => (
                <li key={index} className="search-results-search-suggestion-item" onClick={() => setSearchInput(item)}>
                  <span>{item}</span>
                  <button
                    className="search-results-delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleDeleteKeyword(item);
                    }}
                  >
                    <IoClose />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {isNoResults && (
            <ul className="search-results-search-suggestions">
              <NoResultsFound title={"Search Helper"} subtitle={"Explore new possibilities"} message={"We couldn’t find any results for your search. Try using different keywords or adjusting filters."} onRetry={"Try Again"} onBack={"Go Back"} />
            </ul>
          )}
        </div>



        <SerachCategories clubs={fetchedData.clubs} events={fetchedData.events} packages={fetchedData.packages} />

        {/* {fetchedData.clubs.length === 0 && (
          <>
            <p className="search-pagesection-title">Recommended Clubs for you</p>
            {loading ? (
              <>
                {[...Array(3)].map((_, index) => (
                  <ResultItemSkeleton key={index} />
                ))}

              </>
            ) : (
              <div className="search-recommendation-section">
                <div className="search-recommendation-list" >
                  {reccommendedclub.map((item, index) => (
                    <Link to={generateClubUrl(item.name, item.cityName, item.areaName)} key={index}>
                      <RecommendationSection data={item} />
                    </Link>
                  ))}
                </div>
              </div>

            )}
          </>
        )} */}


        {/* {fetchedData.packages.length === 0 && (
          <>
            <p className="search-pagesection-title">Recommended Packages for you</p>
            {loading ? (
              <>
                {[...Array(3)].map((_, index) => (
                  <ResultItemSkeleton key={index} />
                ))}

              </>
            ) : (
              <div className="search-recommendation-section">
                <div className="search-recommendation-list" >
                  {recommendedPackages.map((item, index) => (
                    <Link to={`/party-packages/${item?.name?.toLowerCase().replace(/\s+/g, '-')}`} key={index}>
                      <RecommendationSection data={item} />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        )} */}

        {/* {fetchedData.events.length === 0 && (
          <>
            <p className="search-pagesection-title">Recommended Events for you</p>
            {loading ? (
              <>
                {[...Array(3)].map((_, index) => (
                  <ResultItemSkeleton key={index} />
                ))}

              </>
            ) : (
              <div className="search-recommendation-section">
                <div className="search-recommendation-list" >
                  {recommendedEvents.map((item, index) => (
                    <Link to={generateEventUrl(item.clubName, item.cityName, item.areaName, item.name)} key={index}>
                      <RecommendationSection data={item} />
                    </Link>
                  ))}
                </div>
              </div>
            )}

          </>
        )} */}


      </div >
    </div >
  );
};

const ResultItem = ({ data }) => (
  <div className="search-page-result-item">
    <img
      src={
        data?.packageThumbnail
          ? url + data.packageThumbnail
          : data?.profile_pic
            ? url + data.profile_pic
            : data?.image
              ? url + data.image
              : defaultImage
      }
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = defaultImage;
      }}
      alt="thumb"
      className="search-page-thumbnail"
    />


    <div className="search-page-info">
      <p className="search-page-title">{data.title || data.name}</p>
      <p className="search-page-subtitle">
        {data.clubName} | <span className="search-page-star">★</span> {data.totalAverageRating} with {data.ratingsCount} ratings
      </p>
    </div>
  </div>
);

const ResultItemSkeleton = () => (
  <div className="search-page-result-item search-page-shimmer">
    <div className="search-page-thumbnail search-page-skeleton-thumbnail" />
    <div className="search-page-info">
      <div className="search-page-skeleton-line search-page-title" />
      <div className="search-page-skeleton-line search-page-subtitle" />
    </div>
  </div>
);

const ExpandableListSection = ({ items = [], renderItem }) => {
  const [showAll, setShowAll] = useState(false);

  const visibleItems = showAll ? items : items.slice(0, 3);

  return (
    <div className="search-pagesection">

      {visibleItems.map((item, index) => (
        <div key={index} className="search-result-item">
          {renderItem(item, index)}
        </div>
      ))}

      {items.length > 3 && (
        <button className="search-page-view-more-btn" onClick={() => setShowAll(!showAll)}>
          {showAll ? 'View Less' : 'View More'}
        </button>
      )}
    </div>
  );
};

const RecommendationSection = ({ data }) => {
  return (

    <div className="search-recommendation-item">
      <img
        src={url + (data?.image || data?.profile_pic || data?.img)}
        alt={data?.name || "recommendation"}
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = defaultImage;
        }}
        className="search-recommendation-item-img"
      />
      <p className="search-recommendation-item-title">{data?.name.slice(0, 8) + "..."}</p>
    </div>

  );
};

